import { RootState } from 'app/store';
import { createSelector } from 'reselect';
import {
  COMPLETENESS_LEVEL_ORDER,
  LISTING_STATUS_ORDER,
  VISIBILITY_REVIEWS_NAV_ID,
  VISIBILITY_SCORE_NAV_ID,
} from '../constants';
import { UberallListingStatus } from '../type';
import { comparisonWindow } from '../utils/visibility.utils';

export const visibilityLinkSelector = ({ visibility }: RootState) => visibility.link.data;
export const isVisibilityLinkLoadingSelector = ({ visibility }: RootState) => visibility.link.isLoading;
export const hasVisibilityLinkErrorSelector = ({ visibility }: RootState) => visibility.link.hasError;
export const hasVisibilityLinkLoadedSelector = ({ visibility }: RootState) => visibility.link.hasLoaded;

export const visibilityLocationIdSelector = createSelector([visibilityLinkSelector], link =>
  typeof link?.locationId === 'number' ? link.locationId : null,
);

export const isVisibilityReadySelector = createSelector(
  [hasVisibilityLinkLoadedSelector, hasVisibilityLinkErrorSelector, visibilityLocationIdSelector],
  (hasLoaded, hasError, locationId) => hasLoaded && !hasError && locationId !== null,
);

export const visibilityScoreSelector = ({ visibility }: RootState) => visibility.score.data;
export const isScoreLoadingSelector = ({ visibility }: RootState) => visibility.score.isLoading;
export const hasScoreErrorSelector = ({ visibility }: RootState) => visibility.score.hasError;
export const hasScoreLoadedSelector = ({ visibility }: RootState) => visibility.score.hasLoaded;

export const scoreTasksSelector = createSelector([visibilityScoreSelector], score => score?.tasks ?? []);

export const profileSelector = ({ visibility }: RootState) => visibility.profile.data;
export const profileDraftSelector = ({ visibility }: RootState) => visibility.profile.draft;
export const isProfileLoadingSelector = ({ visibility }: RootState) => visibility.profile.isLoading;
export const hasProfileErrorSelector = ({ visibility }: RootState) => visibility.profile.hasError;
export const isProfileSavingSelector = ({ visibility }: RootState) => visibility.profile.isSaving;

export const hasProfileChangesSelector = createSelector([profileDraftSelector], draft => Object.keys(draft).length > 0);

export const profileFormSelector = createSelector([profileSelector, profileDraftSelector], (profile, draft) =>
  profile ? { ...profile.location, ...draft } : null,
);

export const completenessSelector = createSelector([profileSelector], profile => profile?.completeness ?? null);

export const missingCompletenessFieldsSelector = createSelector([completenessSelector], completeness => {
  if (!completeness) return [];
  return completeness.levels
    .flatMap(level => level.fields.filter(field => !field.filled).map(field => ({ ...field, level: level.level })))
    .sort((left, right) => right.weight - left.weight);
});

export const seoReportSelector = ({ visibility }: RootState) => visibility.report.data;
export const isSeoReportGeneratingSelector = ({ visibility }: RootState) => visibility.report.isGenerating;
export const isSeoReportSendingSelector = ({ visibility }: RootState) => visibility.report.isSending;
export const isSeoReportDownloadingSelector = ({ visibility }: RootState) => visibility.report.isDownloading;

export const listingsSelector = ({ visibility }: RootState) => visibility.listings.data;
export const isListingsLoadingSelector = ({ visibility }: RootState) => visibility.listings.isLoading;
export const hasListingsErrorSelector = ({ visibility }: RootState) => visibility.listings.hasError;
export const isSyncingListingsSelector = ({ visibility }: RootState) => visibility.listings.isSyncing;

/** Every directory, not just the ones needing action — the tab is labelled with the full count. */
export const orderedListingsSelector = createSelector([listingsSelector], summary => {
  if (!summary) return [];
  const rank = (status: UberallListingStatus) => LISTING_STATUS_ORDER[status] ?? Number.MAX_SAFE_INTEGER;

  return [...summary.listings].sort((left, right) => rank(left.status) - rank(right.status));
});

export const listingCountsSelector = createSelector([listingsSelector], summary => {
  const listings = summary?.listings ?? [];
  const count = (...statuses: UberallListingStatus[]) =>
    listings.filter(listing => statuses.includes(listing.status)).length;

  return {
    total: listings.length,
    synced: count('SYNCED', 'LINKED', 'SUBMITTED'),
    actionRequired: count('ACTION_REQUIRED'),
    inProgress: count('UPDATING', 'SCHEDULED', 'IN_PROGRESS'),
    notStarted: count('NOT_STARTED'),
    unknown: count('UNKNOWN'),
  };
});

export const photosSelector = ({ visibility }: RootState) => visibility.photos.data;
export const isPhotosLoadingSelector = ({ visibility }: RootState) => visibility.photos.isLoading;
export const hasPhotosErrorSelector = ({ visibility }: RootState) => visibility.photos.hasError;
export const isPhotoUploadingSelector = ({ visibility }: RootState) => visibility.photos.isUploading;

export const keywordsSelector = ({ visibility }: RootState) => visibility.keywords.data;
export const isKeywordsLoadingSelector = ({ visibility }: RootState) => visibility.keywords.isLoading;
export const hasKeywordsErrorSelector = ({ visibility }: RootState) => visibility.keywords.hasError;
export const isKeywordsMutatingSelector = ({ visibility }: RootState) => visibility.keywords.isMutating;

export const keywordSlotsSelector = createSelector([keywordsSelector], keywords => {
  if (!keywords) return { used: 0, limit: 0, remaining: 0, isFull: true, isKnown: false };
  return {
    used: keywords.used,
    limit: keywords.limit,
    remaining: keywords.remaining,
    isFull: keywords.remaining <= 0,
    isKnown: true,
  };
});

export const dashboardSelector = ({ visibility }: RootState) => visibility.dashboard.data;
export const dashboardRangeSelector = ({ visibility }: RootState) => visibility.dashboard.range;
export const dashboardTabSelector = ({ visibility }: RootState) => visibility.dashboard.tab;
export const isDashboardLoadingSelector = ({ visibility }: RootState) => visibility.dashboard.isLoading;
export const hasDashboardErrorSelector = ({ visibility }: RootState) => visibility.dashboard.hasError;

export const reviewsSelector = ({ visibility }: RootState) => visibility.reviews.data;
export const reviewsCountSelector = ({ visibility }: RootState) => visibility.reviews.count;
export const reviewsUnansweredCountSelector = ({ visibility }: RootState) => visibility.reviews.unansweredCount;
/**
 * What each navigation entry should show, and nothing about how it is rendered.
 *
 * - LittleBill Score carries the PRIORITY tasks, the ones the merchant is asked to act on first.
 * - Inbox avis carries the unanswered reviews; only a published reply clears one, opening it does not.
 *
 * Paramétrage and Posts are intentionally absent: every open task already shows on the score, and
 * badging its destination as well would count the same work twice.
 */
export const navigationBadgeCountsSelector = createSelector(
  [visibilityScoreSelector, reviewsUnansweredCountSelector],
  (score, unansweredCount): Record<string, number> => {
    const priorityTasks = score?.priorityTasks ?? 0;

    return {
      [VISIBILITY_SCORE_NAV_ID]: priorityTasks,
      [VISIBILITY_REVIEWS_NAV_ID]: unansweredCount,
    };
  },
);

export const hasUnansweredCountLoadedSelector = ({ visibility }: RootState) => visibility.reviews.hasCountLoaded;
export const reviewsPageSelector = ({ visibility }: RootState) => visibility.reviews.page;
export const reviewsPageSizeSelector = ({ visibility }: RootState) => visibility.reviews.size;
export const reviewsFilterSelector = ({ visibility }: RootState) => visibility.reviews.filter;
export const isReviewsLoadingSelector = ({ visibility }: RootState) => visibility.reviews.isLoading;
export const hasReviewsErrorSelector = ({ visibility }: RootState) => visibility.reviews.hasError;
export const selectedReviewIdSelector = ({ visibility }: RootState) => visibility.reviews.selectedId;
export const pendingReplyByIdSelector = ({ visibility }: RootState) => visibility.reviews.pendingReplyById;
export const replyLoadRequestedByIdSelector = ({ visibility }: RootState) => visibility.reviews.replyLoadRequestedById;
export const replyErrorByIdSelector = ({ visibility }: RootState) => visibility.reviews.replyErrorById;
export const aiDraftByIdSelector = ({ visibility }: RootState) => visibility.reviews.aiDraftById;
export const generatingReplyForSelector = ({ visibility }: RootState) => visibility.reviews.generatingFor;

export const selectedReviewSelector = createSelector(
  [reviewsSelector, selectedReviewIdSelector],
  (reviews, selectedId) => reviews.find(review => review.id === selectedId) ?? null,
);

export const replyTemplatesSelector = ({ visibility }: RootState) => visibility.templates.data;

export const serviceAreasSelector = ({ visibility }: RootState) => visibility.serviceAreas.data;
export const isServiceAreasLoadingSelector = ({ visibility }: RootState) => visibility.serviceAreas.isLoading;
export const hasServiceAreasErrorSelector = ({ visibility }: RootState) => visibility.serviceAreas.hasError;
export const isServiceAreasMutatingSelector = ({ visibility }: RootState) => visibility.serviceAreas.isMutating;
export const isSyncingServiceAreasSelector = ({ visibility }: RootState) => visibility.serviceAreas.isSyncing;

export const templatesForSelectedReviewSelector = createSelector(
  [replyTemplatesSelector, selectedReviewSelector],
  (templates, review) => {
    if (!review) return templates;
    const rating = review.rating;
    return templates.filter(template => {
      if (template.minRating !== null && (rating === null || rating < template.minRating)) return false;
      if (template.maxRating !== null && (rating === null || rating > template.maxRating)) return false;
      return true;
    });
  },
);

export const autoResponseRulesSelector = ({ visibility }: RootState) => visibility.autoResponses.data;
export const isAutoResponseRulesLoadingSelector = ({ visibility }: RootState) => visibility.autoResponses.isLoading;
export const hasAutoResponseRulesErrorSelector = ({ visibility }: RootState) => visibility.autoResponses.hasError;
export const isAutoResponseRuleMutatingSelector = ({ visibility }: RootState) => visibility.autoResponses.isMutating;

export const activeAutoResponseTriggersSelector = createSelector([autoResponseRulesSelector], rules =>
  rules
    .filter(rule => rule.status === 'ACTIVE')
    .flatMap(rule => rule.triggers.map(trigger => ({ trigger, ruleId: rule.id, ruleName: rule.name }))),
);

export const aiSettingsSelector = ({ visibility }: RootState) => visibility.aiSettings.data;
export const isAiSettingsSavingSelector = ({ visibility }: RootState) => visibility.aiSettings.isSaving;

export const postsSelector = ({ visibility }: RootState) => visibility.posts.data;
export const postDirectoriesSelector = ({ visibility }: RootState) => visibility.postDirectories.data;
export const isPostDirectoriesLoadingSelector = ({ visibility }: RootState) => visibility.postDirectories.isLoading;
export const hasPostDirectoriesErrorSelector = ({ visibility }: RootState) => visibility.postDirectories.hasError;
export const keywordRankingsSelector = ({ visibility }: RootState) => visibility.keywordRankings.data;
export const hasKeywordRankingsLoadedSelector = ({ visibility }: RootState) => visibility.keywordRankings.hasLoaded;
export const isKeywordRankingsLoadingSelector = ({ visibility }: RootState) => visibility.keywordRankings.isLoading;
export const hasKeywordRankingsErrorSelector = ({ visibility }: RootState) => visibility.keywordRankings.hasError;
export const dashboardComparisonSelector = ({ visibility }: RootState) => visibility.dashboard.comparison;
/** The comparison actually sent, as a string: picking CUSTOM or typing one date leaves it unchanged. */
export const dashboardComparisonKeySelector = ({ visibility }: RootState) =>
  JSON.stringify(comparisonWindow(visibility.dashboard.range, visibility.dashboard.comparison));
export const postsTabSelector = ({ visibility }: RootState) => visibility.posts.tab;
export const isPostsLoadingSelector = ({ visibility }: RootState) => visibility.posts.isLoading;
export const hasPostsErrorSelector = ({ visibility }: RootState) => visibility.posts.hasError;
export const isPostMutatingSelector = ({ visibility }: RootState) => visibility.posts.isMutating;
export const isGeneratingPostDraftSelector = ({ visibility }: RootState) => visibility.posts.isGeneratingDraft;
export const hasPostDraftErrorSelector = ({ visibility }: RootState) => visibility.posts.hasDraftError;
export const postAiDraftSelector = ({ visibility }: RootState) => visibility.posts.aiDraft;

export const socialConnectionsSelector = ({ visibility }: RootState) => visibility.socialConnections.data;
export const isSocialConnectionsLoadingSelector = ({ visibility }: RootState) => visibility.socialConnections.isLoading;
export const hasSocialConnectionsErrorSelector = ({ visibility }: RootState) => visibility.socialConnections.hasError;
export const hasSocialConnectionsLoadedSelector = ({ visibility }: RootState) => visibility.socialConnections.hasLoaded;
export const isSocialConnectionsMutatingSelector = ({ visibility }: RootState) =>
  visibility.socialConnections.isMutating;
